import { Snackbar } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import Button, { IconButtonProps } from '@ui/Button';
import Icon from '@ui/Icon';
import { useClipboard } from '../../../browserUtils';

export interface TextCopyButtonProps
  extends Omit<IconButtonProps, 'children' | 'onClick'> {
  copyText: string;
  onCopyMessage?: string;
}

const COPIED_DISPLAY_TIMEOUT = 2000;

/**
 * @description The `TextCopyButton` copies the text prop to the clipboard while
 * giving the user feedback that the text was copied.
 */
const TextCopyButton = React.forwardRef<HTMLButtonElement, TextCopyButtonProps>(
  ({ copyText, onCopyMessage, ...props }, ref) => {
    const { copyToClipboard } = useClipboard();
    const [copied, setCopied] = useState<boolean>(false);

    const handleCopy = useCallback(() => {
      copyToClipboard(copyText).then(() => {
        setCopied(true);
      });
    }, [copyToClipboard, copyText]);

    const handleAlertClose = useCallback(() => {
      setCopied(false);
    }, []);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (copied) {
        const timeoutId = setTimeout(() => {
          setCopied(false);
        }, COPIED_DISPLAY_TIMEOUT);
        return () => {
          clearTimeout(timeoutId);
        };
      }
    }, [copied]);

    return (
      <>
        <Button
          {...props}
          variant="icon"
          disabled={copied}
          color="default"
          onClick={handleCopy}
          ref={ref}
          data-variant="text_copy"
        >
          <Icon type="copy" />
        </Button>
        <Snackbar
          open={copied}
          autoHideDuration={COPIED_DISPLAY_TIMEOUT}
          onClose={handleAlertClose}
          message={onCopyMessage ?? 'Link copied!'}
        />
      </>
    );
  }
);

export default TextCopyButton;
